import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { StatusTimeline } from "@/components/StatusTimeline";
import { PaymentPanel } from "@/components/PaymentPanel";
import { Button } from "@/components/ui/button";
import { rupees, dateTime, spacedPhone } from "@/lib/format";
import { usePageTitle } from "@/lib/usePageTitle";

// Non-terminal statuses (APPOINTMENT_TRANSITIONS in core/enums.py): keep polling until the
// visit is completed / cancelled / no_show. `checked_in` still awaits the staff "completed".
const OPEN_STATUSES = ["requested", "confirmed", "rescheduled", "checked_in"];
const CANCELLABLE = ["requested", "confirmed", "rescheduled"];
const POLL_MS = 8000;

/** Citizen appointment tracking — `/appointments/:id`. No single-GET exists, so it is derived from `/appointments/mine` (as mobile `useAppointment` does). */
export default function AppointmentDetail() {
  const { id } = useParams();
  const { t, lang } = useApp();
  usePageTitle(t("appointments.detailTitle")); // R5-11: per-page tab title
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | missing | error
  const [loadError, setLoadError] = useState("");
  const [payment, setPayment] = useState(null);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [res, pay] = await Promise.all([
        api.get("/appointments/mine"),
        api.get("/payments/mine").catch(() => null),
      ]);
      const found = rows(res.data).find((x) => String(x.id) === String(id));
      if (!found) {
        setItem(null);
        setState("missing");
        return;
      }
      setItem(found);
      setPayment(
        pay
          ? rows(pay.data).find(
              (p) =>
                p.source_type === "appointment" && p.source_id === found.id,
            ) || null
          : null,
      );
      setState("ready");
    } catch (e) {
      // A blip during background polling must not blank a page the citizen is already reading.
      setLoadError(apiErrorMessage(e, ""));
      setState((s) => (s === "ready" ? s : "error"));
    }
  }, [id]);

  useEffect(() => {
    setState("loading");
    load();
  }, [load]);

  // A UPI payment has no gateway callback; keep asking while it is open.
  useEffect(() => {
    if (
      !payment ||
      !["pending", "awaiting_confirmation"].includes(payment.status)
    )
      return;
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [payment?.status, load]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep asking while the thing is live: staff confirm on their side, the citizen sees it land here.
  useEffect(() => {
    if (!item || !OPEN_STATUSES.includes(item.status)) return;
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [item?.status, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/appointments/${id}/transition`, { status: "cancelled" });
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  const claimPayment = async (utr) => {
    setPaymentBusy(true);
    setPaymentError("");
    try {
      await api.post(`/payments/${payment.id}/claim`, { utr });
      await load();
    } catch (e) {
      setPaymentError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setPaymentBusy(false);
    }
  };

  const cancelPayment = async () => {
    setPaymentBusy(true);
    setPaymentError("");
    try {
      await api.post(`/payments/${payment.id}/cancel`);
      await load();
    } catch (e) {
      setPaymentError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setPaymentBusy(false);
    }
  };

  if (state === "loading")
    return (
      <div
        className="mx-auto max-w-2xl space-y-4 px-5 py-12 sm:px-8"
        data-testid="appointment-skeleton"
        aria-busy="true"
      >
        <div className="h-28 animate-pulse rounded-2xl bg-muted" />
        <div className="h-40 animate-pulse rounded-2xl bg-muted" />
      </div>
    );
  if (state === "missing")
    return (
      <div
        className="mx-auto max-w-2xl px-5 py-24 text-center"
        data-testid="appointment-not-found"
      >
        <p className="text-lg">{t("appointments.notFound")}</p>
        <Button
          className="mt-6 rounded-full"
          onClick={() => navigate("/activity")}
        >
          {t("appointments.backToActivity")}
        </Button>
      </div>
    );
  if (state === "error")
    return (
      <div
        className="mx-auto max-w-2xl px-5 py-24 text-center"
        data-testid="appointment-error"
      >
        <p className="text-lg text-destructive">
          {loadError || t("appointments.loadFailed")}
        </p>
        <Button
          className="mt-6 rounded-full"
          onClick={() => {
            setState("loading");
            load();
          }}
          data-testid="appointment-retry-btn"
        >
          {t("common.retry")}
        </Button>
      </div>
    );

  return (
    <div
      className="mx-auto max-w-2xl px-5 py-12 sm:px-8"
      data-testid="appointment-page"
    >
      <div className="rounded-2xl bg-[hsl(var(--primary))] p-6 text-primary-foreground sm:p-8">
        <p className="text-sm opacity-80">{t("appointments.detailTitle")}</p>
        <h1
          className="mt-1 font-heading text-3xl font-bold tracking-tight"
          data-testid="appointment-headline"
        >
          {t("appointments.statusLabels")[item.status] || item.status}
        </h1>
      </div>

      <div className="mt-6 space-y-6">
        <div className="rounded-2xl border border-border p-5">
          <p
            className="font-heading text-lg font-semibold"
            data-testid="appointment-title"
          >{`${item.doctor_name} · ${item.business_name}`}</p>
          <StatusTimeline
            timeline={item.timeline}
            labelsKey="appointments.statusLabels"
            testId="appointment-timeline"
          />
        </div>

        <div className="rounded-2xl border border-border p-5 text-sm">
          <Row
            label={t("appointments.slotLabel")}
            value={dateTime(item.slot_start_at, lang)}
          />
          <Row
            label={t("appointments.fee")}
            value={rupees({ minor_units: item.fee?.minor_units })}
          />
          <Row
            label={t("appointments.patientName")}
            value={item.patient_snapshot?.name}
          />
          {item.patient_snapshot?.phone && (
            <Row
              label={t("appointments.patientPhone")}
              value={spacedPhone(item.patient_snapshot.phone)}
            />
          )}
          {item.notes && (
            <Row label={t("appointments.notes")} value={item.notes} />
          )}
        </div>

        {payment && (
          <PaymentPanel
            payment={payment}
            requireUtr={payment.kind === "checkout"}
            onClaim={claimPayment}
            onCancel={cancelPayment}
            busy={paymentBusy}
            error={paymentError}
            testId="appointment-payment"
          />
        )}

        {error && (
          <p
            className="text-sm text-destructive"
            data-testid="appointment-error-msg"
          >
            {error}
          </p>
        )}

        {CANCELLABLE.includes(item.status) && (
          <Button
            variant="outline"
            className="rounded-full"
            disabled={busy}
            onClick={cancel}
            data-testid="appointment-cancel-btn"
          >
            {t("common.cancel")}
          </Button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-right">{value}</span>
    </div>
  );
}
